import type { components } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';

export type FplEntrySearchResult =
  components['schemas']['EntrySearchResponse']['data'][number];

export const MIN_NAME_SEARCH_LENGTH = 3;
const DEFAULT_RESULT_LIMIT = 50;

export type FplEntrySearchParams =
  | { teamName: string }
  | { managerName: string }
  | { fplId: number };

/**
 * Builds search params from raw user input: an all-digit query searches by
 * FPL ID, anything else by team name (minimum 3 characters).
 */
export const buildEntrySearchParams = (
  query: string
): FplEntrySearchParams | null => {
  const trimmed = query.trim();
  if (!trimmed) return null;

  if (/^\d+$/.test(trimmed)) {
    const fplId = parseInt(trimmed, 10);
    return fplId > 0 ? { fplId } : null;
  }

  if (trimmed.length < MIN_NAME_SEARCH_LENGTH) return null;

  return { teamName: trimmed };
};

export type FplEntryLeague =
  components['schemas']['EntryLeaguesResponse']['data'][number];

/**
 * Fetches an entry's invitational classic mini-leagues with a standings
 * summary (user rank/points, leader and runner-up points) for the
 * post-GW1 readiness race.
 */
export const fetchEntryLeagues = async (
  fplId: number
): Promise<FplEntryLeague[]> => {
  const { data, error } = await ffhClient().GET('/fpl/entries/{id}/leagues', {
    params: { path: { id: fplId } }
  });

  if (error || !data) {
    const errorBody = error as { error?: { message?: string } } | undefined;
    throw new Error(
      errorBody?.error?.message ?? 'Failed to load your mini-leagues'
    );
  }

  return data.data;
};

export const searchFplEntries = async (
  params: FplEntrySearchParams,
  limit: number = DEFAULT_RESULT_LIMIT
): Promise<FplEntrySearchResult[]> => {
  const { data, error, response } = await ffhClient().GET('/fpl/entries', {
    params: { query: { ...params, limit } }
  });

  if (error || !data) {
    const errorBody = error as { error?: { message?: string } } | undefined;
    const message =
      errorBody?.error?.message ?? 'Failed to search for FPL teams';

    if (response?.status === 400) {
      throw new Error(`Invalid search: ${message}`);
    }
    throw new Error(message);
  }

  return data.data;
};
